import { GridCellParams, GridPreProcessEditCellProps } from '@mui/x-data-grid-premium';
import { GridColDef } from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import { Money } from 'sr-types/lib/production/v1/graphql';
import { useI18n } from '../../i18n/I18n';
import { formatMoney, isMoney, moneyToBigNumber, parseMoney } from '../../utils/money';
import { isNumberValid } from '../../utils/numbers';
import { AdjustmentLine } from './DiscountGrid';
import PricingLine from './PricingLine';
import { UseColDefProps } from './UseColDefProps';

const moneyValueGetter = () => (value) => {
    return isMoney(value) ? moneyToBigNumber(value as Money).toString() : '';
};

const moneyValueSetter =
    (
        calculator,
        name: string,
        prop: string,
        locale: string,
        rows: PricingLine[] | AdjustmentLine[],
        onChange: (pricing: PricingLine[]) => void
    ) =>
    (value, row) => {
        const currencyCode = row.currency;
        const money = currencyCode ? parseMoney(value === '' ? '0' : value, { locale, currencyCode }) : undefined;
        const updatedRow = calculator.calculate({
            ...row,
            [prop]: isMoney(money) ? money : value
        });
        if (row) {
            const updated = rows.map((r: PricingLine | AdjustmentLine) => (r.id === row.id ? updatedRow : r));
            onChange(updated);
        }
        return updatedRow;
    };

const moneyValueParser = () => (value) => {
    return value;
};

const moneyValueFormatter = (locale: string) => (value, row) => {
    const currencyCode = row.currency;
    return currencyCode
        ? formatMoney(parseMoney(value, { locale, currencyCode }), { locale: locale, showCurrencySymbol: true })
        : '';
};

export default function useMoneyColDef(props: UseColDefProps): GridColDef {
    const {
        name,
        prop,
        locale,
        width,
        readOnly = false,
        rightAlign = true,
        flex = 0,
        setIsRowValid,
        calculator,
        classNames,
        rows,
        setRows: onChange
    } = props;
    const header = useI18n(`grid.header.${prop}`);
    const align = rightAlign ? 'right' : 'left';

    return {
        field: prop,
        headerName: header,
        editable: !readOnly,
        flex: flex,
        width: width,
        type: 'string',
        align: align,
        headerAlign: align,
        cellClassName: (params: GridCellParams) => {
            return clsx(prop, classNames, { isEmpty: !readOnly && !isMoney(params.row[params.field]) });
        },
        valueFormatter: moneyValueFormatter(locale),
        valueGetter: moneyValueGetter(),
        valueSetter: readOnly ? undefined : moneyValueSetter(calculator, name, prop, locale, rows, onChange),
        valueParser: readOnly ? undefined : moneyValueParser(),
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
            const valid = isNumberValid(params.props.value, locale);
            if (typeof setIsRowValid === 'function') {
                setIsRowValid(params.id as string, prop, valid);
            }
            return { ...params.props, error: !valid };
        }
    };
}
